import React from 'react';
import { Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';

// Get base URL for public assets
const getPublicUrl = (path: string) => {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
};

const DEFAULT_LOGO = 'https://i.postimg.cc/QVPV9d0x/Full-name-logo-blue.png';
const CREST_LOGO = 'https://i.postimg.cc/MTvXhZ7K/image-12.png';
const CREST_PENTEST_BADGE = 'https://i.postimg.cc/PxPNBfWp/image-13.png';
const CERTIFIED_ICON = getPublicUrl('/achievement-icon.webp');

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    position: 'relative',
  },
  mainContent: {
    flex: 1,
    paddingTop: 50,
    paddingHorizontal: 40,
    paddingBottom: 20,
  },
  greenAccent: {
    position: 'absolute',
    left: 0,
    top: 35,
    width: 5,
    height: 90,
    backgroundColor: '#84cc16',
  },
  title: {
    fontSize: 32,
    fontFamily: 'Helvetica-Bold',
    color: '#1e3a5f',
    marginBottom: 35,
    marginTop: 5,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#1e3a5f',
    marginBottom: 14,
  },
  paragraph: {
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 1.7,
    marginBottom: 14,
    textAlign: 'left',
  },
  certifiedSection: {
    flexDirection: 'row',
    marginTop: 80,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 15,
  },
  certifiedIcon: {
    width: 32,
    height: 32,
    marginTop: 2,
  },
  certifiedLeft: {
    width: '38%',
  },
  certifiedTitle: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    color: '#1e3a5f',
    marginBottom: 8,
  },
  certifiedSubtitle: {
    fontSize: 10,
    color: '#475569',
    marginBottom: 12,
  },
  certifiedText: {
    fontSize: 10,
    color: '#374151',
    lineHeight: 1.6,
  },
  badgesContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 15,
    marginLeft: 40,
  },
  badgeWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeBox: {
    width: 90,
    height: 90,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
  },
  badgeShadow: {
    backgroundColor: '#f1f5f9',
    borderRadius: 14,
    padding: 4,
  },
  crestLogo: {
    width: 60,
    height: 60,
    objectFit: 'contain',
  },
  crestPentestBadge: {
    width: 60,
    height: 60,
    objectFit: 'contain',
  },
  badgeLabel: {
    fontSize: 10,
    color: '#1e3a5f',
    marginTop: 12,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
  },
  footer: {
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
    paddingVertical: 25,
    paddingHorizontal: 55,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 80,
  },
  footerColumn: {
    flexDirection: 'column',
  },
  footerTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: '#1e3a5f',
    marginBottom: 12,
  },
  footerText: {
    fontSize: 10,
    color: '#4B5563',
    marginBottom: 6,
  },
  footerAddressText: {
    fontSize: 10,
    color: '#4b5563',
    marginBottom: 4,
  },
  footerLogoWrapper: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  footerLogo: {
    width: 110,
    height: 40,
    objectFit: 'contain',
  },
});

const AboutUsPage: React.FC = () => (
  <Page size="A4" style={styles.page}>
    {/* Green accent bar */}
    <View style={styles.greenAccent} />

    {/* Main Content */}
    <View style={styles.mainContent}>
      <Text style={styles.title}>About Us</Text>

      <Text style={styles.sectionTitle}>Modern Penetration Testing as a Service</Text>

      <Text style={styles.paragraph}>
        Capture The Bug is a modern Penetration Testing as a Service (PTaaS) platform designed to support fast-moving engineering teams and security-conscious businesses.
      </Text>

      <Text style={styles.paragraph}>
        Built for agility, collaboration, and scale, our platform combines human-led expertise with automation to continuously identify, validate, and triage security vulnerabilities across web, mobile, infra and API environments.
      </Text>

      <Text style={styles.paragraph}>
        Organisations across New Zealand, Australia, and North America - including public companies and high-growth SaaS teams - trust us to secure their CI/CD pipelines, meet compliance goals, and reduce time-to-remediation without slowing development velocity.
      </Text>

      {/* Certified & Trusted Section */}
      <View style={styles.certifiedSection}>
        <Image src={CERTIFIED_ICON} style={styles.certifiedIcon} />

        <View style={styles.certifiedLeft}>
          <Text style={styles.certifiedTitle}>Certified & Trusted</Text>
          <Text style={styles.certifiedSubtitle}>Industry-leading standards</Text>
          <Text style={styles.certifiedText}>
            Capture The Bug is headquartered in New Zealand and proudly works with customers across regulated and innovation-driven sectors.
          </Text>
        </View>

        <View style={styles.badgesContainer}>
          <View style={styles.badgeWrapper}>
            <View style={styles.badgeShadow}>
              <View style={styles.badgeBox}>
                <Image src={CREST_LOGO} style={styles.crestLogo} />
              </View>
            </View>
            <Text style={styles.badgeLabel}>CREST Certified</Text>
          </View>
          <View style={styles.badgeWrapper}>
            <View style={styles.badgeShadow}>
              <View style={styles.badgeBox}>
                <Image src={CREST_PENTEST_BADGE} style={styles.crestPentestBadge} />
              </View>
            </View>
            <Text style={styles.badgeLabel}>Industry Standards</Text>
          </View>
        </View>
      </View>
    </View>

    {/* Footer */}
    <View style={styles.footer}>
      <View style={styles.footerColumn}>
        <Text style={styles.footerTitle}>Contact Information</Text>
        <Text style={styles.footerText}>hello@capturethebug.xyz</Text>
        <Text style={styles.footerText}>ankita@capturethebug.xyz</Text>
        <Text style={styles.footerText}>+64 22 199 4320</Text>
      </View>

      <View style={styles.footerColumn}>
        <Text style={styles.footerTitle}>Address</Text>
        <Text style={styles.footerAddressText}>526 Victoria Street Waikato,</Text>
        <Text style={styles.footerAddressText}>Hamilton, New Zealand</Text>
      </View>

      <View style={styles.footerLogoWrapper}>
        <Image src={DEFAULT_LOGO} style={styles.footerLogo} />
      </View>
    </View>
  </Page>
);

export default AboutUsPage;

